import React, { useState, useEffect } from "react";
import { User, Search, RefreshCw, Key, Shield, CalendarDays, History, ChevronUp, ChevronDown } from "lucide-react";
import baseapi from "../../../services/baseapi"; // Adjust import path
import { showError } from "../../../utils/toast"; // Adjust import path
import { Pagination } from "./Pagination";

interface LogonHistoryRecord {
    id: number;
    user: string;
    logon_domain: string;
    logon_time: string | number;
    logon_type: string;
    authentication_package: string;
    logon_id: string;
    logon_sid: string;
}

interface AssetLogonHistoryProps {
    assetId: string | number;
    client_id: string | number;
    platform?: string;
}

export default function AssetLogonHistory({ assetId, client_id, platform }: AssetLogonHistoryProps) {
    const [history, setHistory] = useState<LogonHistoryRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [searchInput, setSearchInput] = useState("");

    const info = JSON.parse(sessionStorage.getItem("internal_ids") || localStorage.getItem("internal_ids") || "{}");
    const platformStr = (platform || info?.platform || info?.os_name || "").toLowerCase();
    const isWindows = (platformStr === "windows" || platformStr.includes("windows") || (platformStr.startsWith("win") && !platformStr.includes("darwin")));
    const isLinux = platformStr.includes("linux") || platformStr.includes("ubuntu");

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(25);
    const [totalRecords, setTotalRecords] = useState(0);

    // Sorting State
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" } | null>(null);

    // Refresh Key
    const [refreshKey, setRefreshKey] = useState(0);

    const fetchHistory = async () => {
        setLoading(true);
        try {
            const skip = (currentPage - 1) * limit;

            const queryObj: any = {
                asset_id: assetId,
                client_id: client_id
            };

            // Enable search by user or domain
            if (searchTerm) {
                queryObj.$or = [
                    { user: { $ilike: `%${searchTerm}%` } },
                    { logon_domain: { $ilike: `%${searchTerm}%` } }
                ];
            }

            const params: any = {
                skip,
                limit,
                query: JSON.stringify(queryObj)
            };

            // Add API Sorting if requested
            if (sortConfig) {
                params.sort_by = sortConfig.key;
                params.sort_order = sortConfig.direction;
            }

            const response = await baseapi.get("/asset_logon_history", { params });

            if (response.data.status) {
                setHistory(response.data.data || []);
                setTotalRecords(response.data.total || 0);
            } else {
                showError(response.data.message || "Failed to load logon history");
                setHistory([]);
                setTotalRecords(0);
            }
        } catch (error: any) {
            showError(error.message || "Failed to load logon history");
            setHistory([]);
            setTotalRecords(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (assetId) {
            fetchHistory();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, limit, searchTerm, assetId, sortConfig, refreshKey]);

    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setCurrentPage(1);
        setSearchTerm(searchInput.trim());
    };

    const handleSort = (key: string) => {
        setSortConfig((prev) => {
            if (prev?.key === key) {
                return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
            }
            return { key, direction: "asc" };
        });
        setCurrentPage(1); // Reset to page 1 on sort
    };

    // Helper for rendering sorting headers
    const SortableHeader = ({ label, sortKey }: { label: string; sortKey: string }) => {
        const isActive = sortConfig?.key === sortKey;
        return (
            <th
                onClick={() => handleSort(sortKey)}
                className="px-3 py-2 text-[10px] font-bold text-gray-300 uppercase tracking-wider cursor-pointer group select-none"
            >
                <div className="flex items-center gap-1">
                    {label}
                    <div className="flex items-center">
                        {isActive ? (
                            sortConfig.direction === "asc" ? (
                                <ChevronUp size={12} className="text-indigo-400" />
                            ) : (
                                <ChevronDown size={12} className="text-indigo-400" />
                            )
                        ) : (
                            <ChevronDown size={12} className="text-gray-200" />
                        )}
                    </div>
                </div>
            </th>
        );
    };

    // Format the epoch timestamp to a readable date
    const formatTime = (epoch: string | number | null) => {
        if (!epoch || epoch === "0") return "-";
        const num = Number(epoch);
        if (isNaN(num)) return "Invalid Date";

        const date = new Date(num * 1000);
        return date.toLocaleString();
    };

    // Render a nice badge for the Logon Type
    const getLogonTypeBadge = (type: string) => {
        const lower = type?.toLowerCase() || '';

        if (lower.includes('interactive')) {
            return (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-blue-500/10 text-blue-400 border border-blue-500/20 whitespace-nowrap">
                    Interactive
                </span>
            );
        }
        if (lower.includes('service')) {
            return (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-purple-500/10 text-purple-400 border border-purple-500/20 whitespace-nowrap">
                    Service
                </span>
            );
        }
        if (lower.includes('network')) {
            return (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 whitespace-nowrap">
                    Network
                </span>
            );
        }
        return (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-gray-600/30 text-gray-300 border border-gray-600 whitespace-nowrap" title={type}>
                {type || 'Unknown'}
            </span>
        );
    };

    return (
        <div className="flex flex-col text-gray-200 relative p-2 pb-6">
            {/* Header & Toolbar */}
            <div className="py-2 flex items-center justify-between gap-4 shrink-0 mb-3 border-b border-gray-700 pb-3">
                <div className="flex items-center">
                    <div className="p-1.5 bg-orange-600 rounded mr-2.5">
                        <History className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex flex-col">
                        <h2 className="text-sm font-bold text-white leading-tight">Logon History</h2>
                        <p className="text-gray-200 text-[10px] uppercase font-semibold tracking-wider">
                            {loading ? '...' : (totalRecords > 0 ? `${totalRecords} records found` : 'No records found')}
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2">


                    <button
                        onClick={() => {
                            setCurrentPage(1);
                            setSearchInput("");
                            setSearchTerm("");
                            setRefreshKey((prev) => prev + 1);
                        }}
                        disabled={loading}
                        className="p-2 bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white border border-gray-700 rounded-md transition-colors shadow-sm flex items-center justify-center disabled:opacity-50 h-8 cursor-pointer"
                        title="Refresh data"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-orange-500' : ''}`} />
                    </button>
                    <form onSubmit={handleSearchSubmit} className="relative">
                        <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                        <input
                            type="text"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder={isLinux ? "Search user or source..." : "Search user or domain..."}
                            className="w-64 bg-gray-900 border border-gray-700 rounded-md pl-8 pr-7 py-1.5 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-orange-500   transition-colors h-8"
                        />
                    </form>
                    {/* <form onSubmit={handleSearchSubmit} className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-200" />
                        <input
                            type="text"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder="Search user or domain..."
                            className="pl-8 pr-3 py-1.5 rounded-md bg-gray-800 text-xs text-gray-200 border border-gray-600 focus:outline-none focus:border-indigo-500 w-56 transition-colors"
                        />
                    </form> */}

                </div>
            </div>

            {/* Table Area */}
            <div className="bg-gray-900/40 rounded-md border border-gray-700 overflow-hidden flex flex-col relative">
                <div className="overflow-x-auto overflow-y-auto custom-scroll">
                    <table className="w-full table-fixed text-left min-w-[900px]">
                        {isWindows ? (
                            <colgroup>
                                <col className="w-[20%]" />
                                <col className="w-[15%]" />
                                <col className="w-[20%]" />
                                <col className="w-[15%]" />
                                <col className="w-[15%]" />
                                <col className="w-[15%]" />
                            </colgroup>
                        ) : (
                            <colgroup>
                                <col className="w-[25%]" />
                                <col className="w-[20%]" />
                                <col className="w-[25%]" />
                                <col className="w-[15%]" />
                                <col className="w-[15%]" />
                            </colgroup>
                        )}
                        <thead className="bg-gray-800 sticky top-0 z-10">
                            <tr className="border-b border-gray-700">
                                <SortableHeader label="User" sortKey="user" />
                                <SortableHeader label={isLinux ? "Source" : "Domain"} sortKey="logon_domain" />
                                <SortableHeader label="Logon Time" sortKey="logon_time" />
                                <SortableHeader label="Logon Type" sortKey="logon_type" />
                                {isWindows && <SortableHeader label="Auth Package" sortKey="authentication_package" />}
                                <SortableHeader label="Logon ID" sortKey="logon_id" />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-800/50">
                            {loading ? (
                                <tr>
                                    <td colSpan={isWindows ? 6 : 5} className="h-48">
                                        <div className="flex flex-col items-center justify-center space-y-3 h-full">
                                            <div className="w-6 h-6 border-2 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin"></div>
                                        </div>
                                    </td>
                                </tr>
                            ) : history.length === 0 ? (
                                <tr>
                                    <td colSpan={isWindows ? 6 : 5} className="h-48">
                                        <div className="flex flex-col items-center justify-center space-y-2 opacity-50 h-full">
                                            <History size={32} className="text-gray-200 mb-2" />
                                            <p className="text-[11px] font-bold text-gray-200 uppercase tracking-widest">No logon history found</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                history.map((record) => (
                                    <tr key={record.id} className="hover:bg-gray-800/40 transition-colors duration-100">
                                        <td className="px-3 py-2">
                                            <div className="flex items-center gap-2">
                                                <User size={12} className="text-gray-200 shrink-0" />
                                                <div className="flex flex-col min-w-0">
                                                    <span className={`text-[11px] font-bold truncate ${record.user ? 'text-white' : 'text-gray-200 italic'}`} title={record.user}>
                                                        {record.user || "SYSTEM"}
                                                    </span>
                                                    {record.logon_sid && (
                                                        <span className="text-[8px] text-gray-200 font-mono truncate mt-0.5" title={record.logon_sid}>
                                                            {record.logon_sid}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-3 py-2">
                                            <div className="flex items-center gap-1.5 text-gray-300">
                                                <Shield size={10} className="text-indigo-400 shrink-0" />
                                                <span className="text-[10px] font-semibold truncate" title={record.logon_domain}>
                                                    {record.logon_domain || "-"}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-3 py-2">
                                            <div className="flex items-center gap-1.5">
                                                <CalendarDays size={10} className="text-gray-200 shrink-0" />
                                                <span className="text-[10px] text-gray-300">
                                                    {formatTime(record.logon_time)}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-3 py-2">
                                            {getLogonTypeBadge(record.logon_type)}
                                        </td>
                                        {isWindows && (
                                            <td className="px-3 py-2">
                                                <div className="flex items-center gap-1.5">
                                                    <Key size={10} className="text-gray-200 shrink-0" />
                                                    <span className="text-[10px] text-gray-300 font-mono truncate" title={record.authentication_package}>
                                                        {record.authentication_package || "-"}
                                                    </span>
                                                </div>
                                            </td>
                                        )}
                                        <td className="px-3 py-2">
                                            <span className="text-[10px] text-gray-200 font-mono bg-gray-900/50 px-1.5 py-0.5 rounded border border-gray-700">
                                                {record.logon_id || "-"}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <Pagination
                    currentPage={currentPage}
                    totalPages={Math.max(1, Math.ceil(totalRecords / limit))}
                    onPageChange={setCurrentPage}
                    itemsPerPage={limit}
                    totalItems={totalRecords}
                    onItemsPerPageChange={(val: number) => {
                        setLimit(val);
                        setCurrentPage(1);
                    }}
                />
            </div>
        </div>
    );
}